import { AdaptedGroup } from "../models";

/**
 * Resolves the **total capacity** for a group, preferring the value reported by
 * the external system (`externalStats.capacity`) and falling back to the
 * group's own `capacity` field.
 *
 * @param group Partial<AdaptedGroup>
 *
 * @returns The capacity to display/use as the group's total slots.
 */
export const getTotal = (group: Partial<AdaptedGroup>): number => {
  return group?.externalStats?.capacity ?? group?.capacity;
};

/**
 * Resolves the **waiting-list count** for a group, preferring the value
 * reported by the external system (`externalStats.waitingListCount`) and
 * falling back to the local enrollment stats (`enrollmentStats.waitingListsCount`).
 *
 * @param group Partial<AdaptedGroup>
 *
 * @returns The number of students currently on the group's waiting list.
 */
export const getWaiting = (group: Partial<AdaptedGroup>): number => {
  return group?.externalStats?.waitingListCount ?? group?.enrollmentStats?.waitingListsCount;
};

/**
 * Resolves the **used capacity** for a group (a.k.a. how many slots are
 * effectively occupied), preferring the value reported by the external system
 * (`externalStats.usedCapacity`) and falling back to the local enrollment
 * stats (`enrollmentStats.usedCapacity`).
 *
 * This is the source of truth for what the user sees as "ocupado" / "inscritos"
 * across the Groups Manager UI, so any validation that needs to compare against
 * the group's current occupancy should rely on this helper to stay aligned
 * with the visible state.
 *
 * @param group Partial<AdaptedGroup>
 *
 * @returns The number of slots currently in use for the group.
 */
export const getUsed = (group: Partial<AdaptedGroup>): number => {
  return group?.externalStats?.usedCapacity ?? group?.enrollmentStats?.usedCapacity;
};
